import React from 'react';
import { Card, Col, Row } from 'antd';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
} from 'recharts';
import { ProjectStatisticsDto, CategoryStatDto } from 'api/models';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from 'utility/utility';

interface ProjectStatisticsChartProps {
  statistics?: ProjectStatisticsDto;
  loading?: boolean;
}

const COLORS = ['#3b82f6', '#22c55e', '#f59e0b'];

export const ProjectStatisticsChart: React.FC<ProjectStatisticsChartProps> = ({ statistics, loading }) => {
  const { t } = useTranslation();

  if (!statistics) {
    return null;
  }

  // Convert statistics into plain numbers and handle the "{{count}}" interpolation
  const unitsByCategory =
    statistics.unitStatistics?.map((stat: CategoryStatDto) => ({
      name: stat.categoryName,
      total: Number(stat.totalUnits) || 0,
      available: Number(stat.availableUnits) || 0,
      sold: Number(stat.soldUnits) || 0,
    })) || [];

  const paymentsData = [
    {
      name: t('projects:paid', 'Paid'),
      value: Number(statistics.projectExpenseses?.paidAmount) || 0,
    },
    {
      name: t('projects:pending', 'Pending'),
      value: Number(statistics.projectExpenseses?.pendingAmount) || 0,
    },
    {
      name: t('projects:overdue', 'Overdue'),
      value: Number(statistics.projectExpenseses?.overdueAmount) || 0,
    },
  ].filter(item => item.value > 0);

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div style={{ background: 'white', padding: '16px', border: '1px solid #ccc', borderRadius: '4px' }}>
          <p style={{ margin: 0, fontWeight: 500 }}>{label}</p>
          {payload.map((entry: any, index: number) => (
            <p key={index} style={{ margin: '4px 0', color: entry.fill }}>
              {entry.name}: {entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const CustomPieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }: any) => {
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos((-midAngle * Math.PI) / 180);
    const y = cy + radius * Math.sin((-midAngle * Math.PI) / 180);

    if (percent < 0.05) return null;

    return (
      <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" style={{ fontSize: '12px' }}>
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  return (
    <Row gutter={[16, 16]}>
      <Col xs={24} xl={14}>
        <Card title={t('projects:units-by-category', 'Units by Category')} loading={loading}>
          <div style={{ width: '100%', height: '400px', minHeight: '400px' }}>
            <ResponsiveContainer>
              <BarChart data={unitsByCategory} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ paddingTop: '20px' }} />
                <Bar dataKey="total" fill={COLORS[0]} name={t('projects:total', 'Total')} />
                <Bar dataKey="available" fill={COLORS[1]} name={t('projects:available', 'Available')} />
                <Bar dataKey="sold" fill={COLORS[2]} name={t('projects:sold', 'Sold')} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </Col>

      <Col xs={24} xl={10}>
        <Card title={t('projects:project-expenses', 'Project Expenses')} loading={loading}>
          <div style={{ width: '100%', height: '400px', minHeight: '400px' }}>
            <ResponsiveContainer>
              <PieChart margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <Pie
                  data={paymentsData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={CustomPieLabel}
                  outerRadius={120}
                  dataKey="value"
                >
                  {paymentsData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value: any) => formatCurrency(value as number)}
                  contentStyle={{ background: 'white', border: '1px solid #ccc', borderRadius: '4px' }}
                />
                <Legend formatter={(value, entry: any) => [value, ' ' + formatCurrency(entry.payload.value)]} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </Col>
    </Row>
  );
};
